import '../styles/pages/ProductPageShared.css';
import '../styles/pages/LandPlotPage.css';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { fadeUp, staggerContainer, staggerItem, viewport } from '../motion/variants';

const locations = ['Kibaha', 'Mlandizi', 'Chalinze', 'Bagamoyo', 'Mapinga'];

const heroStats = [
  { value: '4', label: 'Plot categories' },
  { value: 'TSh 1.5M+', label: 'Starting price' },
  { value: '5', label: 'Growth corridors' },
];

const plotTiers = [
  {
    id: 'entry-level',
    title: 'Entry Level Plots',
    price: 'TSh 1.5M – 5M',
    imageSrc: '/img/land1.jpeg',
    imageAlt: 'Entry level village land plots for long-term investment',
    intro:
      'Affordable village and undeveloped plots suitable for long-term investment and future growth.',
    introSecondary:
      'Ideal for future resale opportunities, small-scale farming, and early property investment.',
  },
  {
    id: 'low-mid-range',
    title: 'Low–Mid Range Plots',
    price: 'TSh 5M – 25M',
    imageSrc: '/img/land2.jpeg',
    imageAlt: 'Low to mid range land plots in growing settlement areas',
    intro:
      'More accessible and moderately developed plots located in growing residential and investment areas.',
    introSecondary:
      'Ideal for future housing projects, small-scale farming, and rental land investments.',
  },
  {
    id: 'mid-range',
    title: 'Mid Range Plots',
    price: 'TSh 25M – 150M',
    imageSrc: '/img/land3.jpeg',
    imageAlt: 'Mid range surveyed land plots in fast-growing development zones',
    intro:
      'Fast-growing investment and development plots located in strategic urban and commercial zones.',
    introSecondary:
      'Suitable for residential homes, commercial developments, rental housing, and business investments.',
  },
  {
    id: 'high-value',
    title: 'High Value Plots',
    price: 'TSh 150M+',
    imageSrc: '/img/land4.jpeg',
    imageAlt: 'High value prime land for commercial and large-scale development',
    intro:
      'Premium land designed for commercial, industrial, and large-scale development projects.',
    introSecondary:
      'Perfect for warehouses, housing estates, tourism developments, and logistics investments.',
  },
];

function LandPlotPage() {
  return (
    <main className="land-plot-page" id="land-plot">
      <section className="product-page-hero land-plot-hero" aria-labelledby="land-plot-heading">
        <img
          className="product-page-hero-bg"
          src="/img/land.jpeg"
          alt=""
          aria-hidden="true"
          loading="eager"
        />
        <div className="product-page-hero-inner">
          <m.span
            className="product-page-hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            Investment Opportunities
          </m.span>
          <m.h1
            id="land-plot-heading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
          >
            Land Plot
          </m.h1>
          <m.p
            className="product-page-hero-accent"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            Kibaha, Mlandizi, Chalinze, Bagamoyo & Mapinga
          </m.p>
          <m.p
            className="product-page-hero-lead"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            Explore affordable and strategically located land plots across Tanzania&apos;s fastest-growing
            corridors. From entry-level village plots to prime commercial zones, we help investors find
            secure land with strong future returns.
          </m.p>
          <m.div
            className="product-page-hero-stats"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            {heroStats.map((stat) => (
              <div key={stat.label} className="product-page-hero-stat">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </m.div>
        </div>
      </section>

      <section className="land-plot-locations" aria-label="Available locations">
        <div className="land-plot-locations-inner">
          {locations.map((location) => (
            <span key={location} className="land-plot-location-chip">
              {location}
            </span>
          ))}
        </div>
      </section>

      <section className="land-plot-body" aria-labelledby="land-plot-tiers-heading">
        <div className="land-plot-body-inner">
          <m.div
            className="product-page-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <h2 id="land-plot-tiers-heading">Plot Categories & Price Ranges</h2>
            <p>
              Choose the investment level that matches your goals — from affordable village plots to
              premium commercial land.
            </p>
          </m.div>

          <m.div
            className="land-plot-tiers"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={staggerContainer}
          >
            {plotTiers.map((tier, index) => (
              <m.article key={tier.id} className="land-plot-tier-card" variants={staggerItem}>
                <div className="land-plot-tier-media">
                  <span className="land-plot-tier-badge">Category {index + 1}</span>
                  <img src={tier.imageSrc} alt={tier.imageAlt} loading="lazy" />
                </div>
                <div className="land-plot-tier-content">
                  <header className="land-plot-tier-header">
                    <h3>{tier.title}</h3>
                    <span className="land-plot-tier-price">{tier.price}</span>
                  </header>
                  <p className="land-plot-tier-intro">{tier.intro}</p>
                  <p className="land-plot-tier-intro land-plot-tier-intro-secondary">
                    {tier.introSecondary}
                  </p>
                  <Link className="land-plot-tier-link" to="/contact">
                    Inquire about this plot
                  </Link>
                </div>
              </m.article>
            ))}
          </m.div>
        </div>
      </section>
    </main>
  );
}

export default LandPlotPage;
